type Echo = { x: number; y: number; born: number; strength: number; phase: number };

/** Decorative pointer signal. Never captures input or runs an idle animation loop. */
export function startWaveTrail(canvas: HTMLCanvasElement): () => void {
  const context = canvas.getContext('2d');
  if (!context) return () => {};
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const lifetime = 1400;
  let echoes: Echo[] = [];
  let frame = 0;
  let width = 0;
  let height = 0;
  let lastPoint: { x: number; y: number; time: number } | null = null;
  const color = getComputedStyle(canvas).getPropertyValue('--blue').trim() || 'oklch(69% 0.18 237)';

  const clear = () => {
    cancelAnimationFrame(frame);
    frame = 0;
    echoes = [];
    lastPoint = null;
    context.clearRect(0, 0, width, height);
  };

  const resize = () => {
    clear();
    width = window.innerWidth;
    height = window.innerHeight;
    // Bound memory and drawing work on large or high-density screens.
    const ratio = Math.min(window.devicePixelRatio || 1, 1.5, Math.sqrt(3_000_000 / Math.max(1, width * height)));
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
  };

  const draw = (now: number) => {
    frame = 0;
    context.clearRect(0, 0, width, height);
    echoes = echoes.filter(echo => now - echo.born < lifetime);
    context.strokeStyle = color;
    context.lineCap = 'round';
    for (const echo of echoes) {
      const age = Math.max(0, (now - echo.born) / lifetime);
      const opacity = .12 * Math.min(1, age * 12) * Math.pow(1 - age, 2);
      const span = Math.min(width * .75, 360) * (1 + age * .1);
      const amplitude = (30 + echo.strength * 38) * (1 - age * .3);
      context.globalAlpha = opacity;
      context.lineWidth = 1.5;
      context.beginPath();
      // Mirrored, tapered peaks echo the luminous signal in the brand mark.
      for (let i = 0; i <= 60; i++) {
        const t = i / 60;
        const envelope = Math.pow(Math.sin(t * Math.PI), 2.4);
        const oscillation = .22 + .78 * Math.abs(Math.sin(t * 25 + echo.phase + age * 1.2));
        const bar = 1 + amplitude * envelope * oscillation;
        const x = echo.x + (t - .5) * span;
        context.moveTo(x, echo.y - bar);
        context.lineTo(x, echo.y + bar);
      }
      context.stroke();
      context.globalAlpha = opacity * .7;
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(echo.x - span / 2, echo.y);
      context.lineTo(echo.x + span / 2, echo.y);
      context.stroke();
    }
    context.globalAlpha = 1;
    if (echoes.length) frame = requestAnimationFrame(draw);
  };

  const move = (event: PointerEvent) => {
    if (motion.matches || document.hidden || !event.isPrimary) return;
    const now = performance.now();
    const distance = lastPoint ? Math.hypot(event.clientX - lastPoint.x, event.clientY - lastPoint.y) : 50;
    // Sampling by distance and time keeps trails airy and caps pointer-event work.
    if (lastPoint && (distance < 28 || now - lastPoint.time < 65)) return;
    echoes.push({ x: event.clientX, y: event.clientY, born: now, strength: Math.min(distance / 150, 1), phase: now * .002 });
    echoes = echoes.slice(-7);
    lastPoint = { x: event.clientX, y: event.clientY, time: now };
    if (!frame) frame = requestAnimationFrame(draw);
  };
  const leave = () => { lastPoint = null; };
  const visibility = () => { if (document.hidden) clear(); };
  resize();
  window.addEventListener('pointermove', move, { passive: true });
  window.addEventListener('pointerdown', move, { passive: true });
  window.addEventListener('pointerup', leave, { passive: true });
  window.addEventListener('pointercancel', leave, { passive: true });
  window.addEventListener('resize', resize, { passive: true });
  window.addEventListener('blur', clear);
  document.documentElement.addEventListener('pointerleave', leave);
  document.addEventListener('visibilitychange', visibility);
  motion.addEventListener('change', clear);
  return () => {
    clear();
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerdown', move);
    window.removeEventListener('pointerup', leave);
    window.removeEventListener('pointercancel', leave);
    window.removeEventListener('resize', resize);
    window.removeEventListener('blur', clear);
    document.documentElement.removeEventListener('pointerleave', leave);
    document.removeEventListener('visibilitychange', visibility);
    motion.removeEventListener('change', clear);
  };
}
